import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'store';
import { setTimecardDuration } from 'store/reducers/timecard';
import { TimecardDetails } from 'types/timecards';
import { updateDocumentTitle } from 'utils/documentTitle';

type Props = {
    timecard: TimecardDetails;
};

const getSecondsSinceDate = (date: Date): number => (new Date().getTime() - date.getTime()) / 1000;

const formatDuration = (timeInSeconds: number): string => {
    const hh = Math.floor(timeInSeconds / 3600)
        .toString()
        .padStart(2, '0');
    const mm = Math.floor((timeInSeconds % 3600) / 60)
        .toString()
        .padStart(2, '0');
    const ss = Math.floor(timeInSeconds % 60)
        .toString()
        .padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
};

const getCurrentDurationInSeconds = (timecard: TimecardDetails, isRunning: boolean): number => {
    if (isRunning) {
        // Validate stopwatchRunningSince before using it
        const startDate = new Date(timecard.stopwatchRunningSince ?? NaN);
        const isValidDate = !isNaN(startDate.getTime());
        const elapsedSeconds = isValidDate ? getSecondsSinceDate(startDate) : 0;

        // Sanity check: elapsed time should be positive and reasonable (not more than 1 year)
        const ONE_YEAR_IN_SECONDS = 365 * 24 * 60 * 60;
        if (isValidDate && elapsedSeconds >= 0 && elapsedSeconds < ONE_YEAR_IN_SECONDS) {
            return timecard.totalTimeInSeconds + elapsedSeconds;
        }
        // Fallback to just showing totalTimeInSeconds if date is invalid or unreasonable
        return timecard.totalTimeInSeconds;
    } else {
        return timecard.totalTimeInSeconds;
    }
};

const TrackingTimer = ({ timecard }: Props) => {
    const duration = useSelector((state) => state.timecard.timecardDuration);
    const isStopwatchRunning = useSelector((state) => state.timecard.isStopwatchRunning);
    const dispatch = useDispatch();
    const previousIsRunningRef = useRef<boolean>(isStopwatchRunning);

    // TODO(react-doctor: no-cascading-set-state): owns the interval lifecycle
    // (clearInterval on unmount); duration dispatch + document title updates
    // are coupled and must run on each tick — cannot move out of an effect.
    useEffect(() => {
        let interval: NodeJS.Timeout | null = null;
        const wasRunning = previousIsRunningRef.current;
        const isNowStopped = wasRunning && !isStopwatchRunning;

        // When transitioning from running to stopped, calculate the final duration
        // using the elapsed time calculation to avoid showing stale totalTimeInSeconds
        const currentDuration = isNowStopped
            ? getCurrentDurationInSeconds(timecard, true) // Calculate as if still running
            : getCurrentDurationInSeconds(timecard, isStopwatchRunning);

        dispatch(setTimecardDuration(formatDuration(currentDuration)));

        if (isStopwatchRunning) {
            updateDocumentTitle(formatDuration(currentDuration));
            interval = setInterval(() => {
                const intervalDuration = getCurrentDurationInSeconds(timecard, isStopwatchRunning);
                dispatch(setTimecardDuration(formatDuration(intervalDuration)));
                updateDocumentTitle(formatDuration(intervalDuration));
            }, 1000);
        } else {
            updateDocumentTitle();
        }

        // Update the ref for the next render
        previousIsRunningRef.current = isStopwatchRunning;

        return () => {
            if (interval) {
                clearInterval(interval);
                updateDocumentTitle();
            }
        };
    }, [timecard, dispatch, isStopwatchRunning]);

    return <span>{duration}</span>;
};

export default TrackingTimer;
